//slots?unitId=69&itemId=710094&kitId=710094&startDate=2022-01-01&endDate=2022-01-07

import { toast } from 'react-toastify';
import { Patient } from '../models/Patient';
import { ScheduleRequest } from '../models/ScheduleRequest';
import { ScheduleSpecialistRequest } from '../models/ScheduleSpecialistRequest';
import { TelemedicineSlot } from '../models/TeleorientationSlots';
import { slotsApi, BusnessRule, teleorientationApi, ScheduleSpecialistApi } from '../services/api';
import { ScheduleCreateChannels } from '../utils/enums/channels';
import { ResponseAPI } from './responseApi';
import { isAxiosError } from '../utils/errors';
import { SlotUnavailableError } from '../errors/SlotUnavailableError';
import { SystemUnavailableError } from '../errors';
import { AxiosError } from 'axios';
import { MedicalRecorderError } from '../errors/MedicalRecorderError';
import { InternalStatusCode } from '../utils/enums/validate-rules.enum';

export enum APPOINTMENT_TYPE {
  'PROCEDURE' = 1,
  'EXAM' = 2,
  'CONSULTATION' = 3,
  'IMMUNIZATION' = 4
}

export enum ATTENDANCE_TYPE {
  'TELEMEDICINE' = 1,
  'PRESENTIAL' = 2,
  'EINSTEIN_TO_YOU' = 3,
  'DRIVE-THRU' = 4
}

enum PAYMENT_TYPE {
  'PARTICULAR' = 1,
  'B2B' = 2,
  'HEALTH_INSURANCE' = 3
}

type SlotProps = {
  unitId: string;
  itemId: string;
  isKit: boolean;
  startDate?: string;
  endDate?: string;
  date?: string;
  resourceId?: string | number | null;
  localId?: string;
};

export type SlotHour = {
  date: string;
  hour: string;
  scheduleId?: string;
  localId?: number | string;
  employeeId?: number;
  serviceId?: number;
  weekDay?: string;
  resource?: string;
  resourceId?: string;
};

type EAVAddressProps = {
  street: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  number: string;
  complement: string;
};

interface PatientEAV extends Omit<Patient, 'healthInsuranceId'> {
  address: EAVAddressProps;
}

type ScheduleObjProps = {
  itemId: string;
  productId: string;
  productName: string;
  careType: ATTENDANCE_TYPE;
  appointmentType: APPOINTMENT_TYPE;
  catalogItemId: string;
  itemName: string;
  salesForceId: string;
};

export interface EAVSlotsProps {
  patient: Omit<PatientEAV, 'healthInsuranceId'>;
  schedules: ScheduleObjProps[];
  payment: {
    type: PAYMENT_TYPE;
    price: number;
    plan_code?: string;
    health_insurance_code?: string;
    office_code?: string;
  };
}

type EAVPayloadProps = {
  patient: string;
  selectedSlotOptions: {
    start: string;
    end: string;
  };
};

export type EAVPatientPayloadProps = {
  patient: {
    firstName: string;
    lastName: string;
    document: string;
    documentType: string;
    birthDate: string;
    phoneNumber: string;
    email: string;
    sex: string;
  };
};

export const fetchDateSlots = async ({ unitId, itemId, isKit, startDate, endDate, resourceId, localId }: SlotProps) => {
  let payload = {
    unitId,
    itemId,
    isKit,
    startDate,
    endDate,
    resourceId
  } as SlotProps;

  if (localId) payload = { ...payload, localId: localId };
  const response = await slotsApi.get('slots/dates', {
    params: payload
  });

  const { data } = response;
  return data;
};

export const fetchSlotsByDate = async ({
  unitId,
  itemId,
  isKit,
  date,
  resourceId,
  localId
}: SlotProps & { hour?: string }): Promise<SlotHour[]> => {
  let params = {
    unitId,
    itemId,
    isKit,
    date,
    resourceId
  } as SlotProps & { hour?: string };

  if (localId) params = { ...params, localId: localId };
  const response = await slotsApi.get('slots/hours', {
    params
  });

  const { availableSchedules } = response.data;
  return availableSchedules;
};

export const fetchSlotsSpecialistDates = async (props: {
  professionalId: number;
  companyId: number;
  medicalProcedureIds: number;
  startDate: string;
  endDate: string;
  specialistId: string;
  officeId: string;
  attendanceType: string;
  appointmentType: string;
  findThreeDates: boolean;
  healthPlanId?: string;
}) => {
  const result: ResponseAPI<string[]> = {
    error: false,
    errorStatus: null,
    message: '',
    data: [] as string[]
  };

  try {
    const response = await ScheduleSpecialistApi.get('slots/dates', {
      params: props
    });

    if (response?.data?.dates?.length) {
      result.data = response?.data?.dates;
    }
    return result;
  } catch (error) {
    toast.error('Sem horários disponíveis.');
    console.error(error);
    result.error = true;
    return result;
  }
};

export const fetchSlotsSpecialistHours = async (props: {
  professionalId: number;
  companyId: number;
  medicalProcedureIds: number;
  date: string;
  specialistId: string;
  officeId: string;
  attendanceType: string;
  appointmentType: string;
  healthPlanId?: string;
}) => {
  const result: ResponseAPI<{ interval: number; hours: string[] }> = {
    error: false,
    errorStatus: null,
    message: '',
    data: {} as { interval: number; hours: string[] }
  };

  try {
    const response = await ScheduleSpecialistApi.get('slots/hours', {
      params: props
    });

    result.data = response?.data;
    return result;
  } catch (error) {
    toast.error('Sem horários disponíveis.');
    console.error(error);
    result.error = true;
    return result;
  }
};

export const saveSchedule = async (schedule: ScheduleRequest) => {
  try {
    const response = await slotsApi.post('schedules', schedule, {
      headers: {
        channel: ScheduleCreateChannels.SCHEDULE_PLATFORM_WEB.toString()
      }
    });
    const { data } = response;
    return data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      const message = error.response?.data.message;

      if (message === 'Horário não disponível') {
        throw new SlotUnavailableError(message);
      }

      const codeError = error.response?.data?.code;
      if (codeError === InternalStatusCode.MEDICAL_RECORD_EXCEPTION) throw new MedicalRecorderError(error.message);
    }

    throw new SystemUnavailableError(error.message);
  }
};

export const fetchAppointment = async (document: string, token: string) => {
  const result: ResponseAPI<any> = {} as ResponseAPI<any>;
  try {
    const response = await slotsApi.get('schedules', {
      params: {
        document,
        verificationCode: token
      }
    });
    if (response.data) result.data = response.data;
  } catch (error) {
    console.error(error);
    result.error = true;
  }
  return result;
};

export const fetchAppointmentSpecialist = async (document: string, token: string, documentType: string) => {
  const result: ResponseAPI<any> = {} as ResponseAPI<any>;
  try {
    const response = await BusnessRule.get('/specialist/schedules/web', {
      params: {
        document,
        documentType,
        verificationCode: token
      }
    });
    if (response.data) result.data = response.data;
  } catch (error: any) {
    console.error(error);
    result.error = true;
    result.message = (error as AxiosError).response?.data.message || error.message;
  }
  return result;
};

export const removeAppointment = async (id: string | number, verificationCode: string) => {
  const response = await slotsApi.delete(`schedules/${verificationCode}/${id}`);
  const { data } = response;
  return data;
};

export const cancelAppointment = async (document: string, code: string) => {
  const response = await slotsApi.delete(`schedules/cancel/${document}/${code}`);
  const { data } = response;
  return data;
};

export const removeAppointmentSpecialist = async (id: string | number, verificationCode: string) => {
  const response = await BusnessRule.delete(`/specialist/schedules/${verificationCode}/${id}`);
  const { data } = response;
  return data;
};

export const checkSalesForceSlots = async (eavSlots: EAVSlotsProps) => {
  const response = await BusnessRule.post('specialist/eav/slots', eavSlots).catch(function (error) {
    if (error.response) {
      toast.error(error.response.data?.message);
      return;
    }
    toast.error('Houve um erro ao procurar agenda!');
  });

  if (!response) {
    return undefined;
  }

  const { data }: any = response;

  return data;
};

type ScheduleSalesForceResponse = {
  code: string;
};

export const saveSalesForceSchedule = async (eavPayload: EAVPayloadProps) => {
  const result: ResponseAPI<ScheduleSalesForceResponse> = {
    error: false,
    errorStatus: null,
    message: '',
    data: {} as ScheduleSalesForceResponse
  };

  try {
    const response = await BusnessRule.post('specialist/eav/schedule', eavPayload, {
      headers: {
        channel: ScheduleCreateChannels.SCHEDULE_PLATFORM_WEB.toString()
      }
    });

    result.data = response.data;
    return result;
  } catch (error) {
    console.error(error);
    result.error = true;
    return result;
  }
};

const SELF_SCHEDULE_ORIGIN_ID = 'AUTO_SCHEDULE';

export const saveSpecialistSchedule = async (schedule: ScheduleSpecialistRequest) => {
  const response = await ScheduleSpecialistApi.post('schedules', schedule, {
    headers: {
      channel: ScheduleCreateChannels.SCHEDULE_PLATFORM_WEB.toString(),
      'x-origin-platform': SELF_SCHEDULE_ORIGIN_ID.toString()
    }
  });
  const { data } = response;
  return data;
};

export const saveTeleorientationSchedule = async (schedule: ScheduleRequest) => {
  const response = await BusnessRule.post('scheduler-svc/teleorientation/schedule', schedule, {
    headers: {
      channel: ScheduleCreateChannels.SCHEDULE_PLATFORM_WEB.toString()
    }
  });
  const { data } = response;
  return data;
};

export const fetchSlotsTeleorientation = async (props: { startDate: string; endDate: string; specialtyId: number }) => {
  const result: ResponseAPI<TelemedicineSlot[]> = {
    error: false,
    errorStatus: null,
    message: '',
    data: [] as TelemedicineSlot[]
  };
  try {
    const response = await BusnessRule.get('scheduler-svc/teleorientation/slots', {
      params: props
    });
    return { ...result, data: response.data as TelemedicineSlot[] };
  } catch (error) {
    return { ...result, error: true };
  }
};

export const saveSalesForceEAV = async (eavPayload: EAVPatientPayloadProps) => {
  const result = {
    error: false,
    errorStatus: null,
    message: '',
    data: {}
  };

  try {
    const response = await BusnessRule.post('/specialist/eav/register-patient', eavPayload);

    result.data = response.data;
    return result;
  } catch (error) {
    console.error(error);
    result.error = true;
    return result;
  }
};
